
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, ExternalLink } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  
  const promptEngineeringResources = [
    {
      title: "Claude Prompt Engineering Guide",
      url: "https://docs.anthropic.com/en/docs/build-with-claude/prompt-engineering/overview",
      description: "Learn how to craft effective prompts for Claude models and understand key prompt engineering concepts from Anthropic's official documentation.",
      provider: "Anthropic"
    },
    {
      title: "OpenAI Prompt Engineering Guide",
      url: "https://platform.openai.com/docs/guides/prompt-engineering",
      description: "Comprehensive guidance on creating effective prompts for GPT models, including best practices and techniques from OpenAI.",
      provider: "OpenAI"
    },
    {
      title: "Learn Prompting",
      url: "https://learnprompting.org/docs/introduction",
      description: "An educational resource focused on teaching prompt engineering fundamentals with practical examples and lessons for various use cases.",
      provider: "Learn Prompting"
    },
    {
      title: "Google Cloud Prompt Engineering Guide",
      url: "https://cloud.google.com/discover/what-is-prompt-engineering",
      description: "Google's perspective on prompt engineering techniques and methodologies for working with large language models.",
      provider: "Google Cloud"
    }
  ];
  
  const filteredResources = searchTerm 
    ? promptEngineeringResources.filter(resource => 
        resource.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        resource.description.toLowerCase().includes(searchTerm.toLowerCase()) || 
        resource.provider.toLowerCase().includes(searchTerm.toLowerCase())
      ) 
    : promptEngineeringResources;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <header className="mb-10">
            <h1 className="text-3xl font-bold">Prompt Engineering Resources</h1>
            <p className="mt-2 text-muted-foreground">
              Essential guides and documentation for mastering prompt engineering techniques
            </p>
          </header>
          
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Blog content - reduced width */}
            <div className="lg:w-2/3">
              {/* Search bar */}
              <div className="mb-6 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search resources..." className="pl-10" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              </div>
              
              {/* Resource list */}
              <div className="space-y-6">
                {filteredResources.length === 0 ? (
                  <div className="text-center py-12 bg-muted/20 rounded-lg">
                    <h3 className="text-xl font-medium mb-2">No resources found</h3>
                    <p className="text-muted-foreground">Try adjusting your search</p>
                  </div>
                ) : (
                  filteredResources.map((resource, index) => (
                    <a 
                      key={index} 
                      href={resource.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="block bg-card hover:bg-card/90 transition-colors border border-border/50 rounded-lg overflow-hidden p-6"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h2 className="text-xl font-bold flex items-center">
                          {resource.title}
                          <ExternalLink className="ml-2 h-4 w-4 text-muted-foreground" />
                        </h2>
                        <Badge>{resource.provider}</Badge>
                      </div>
                      <p className="text-muted-foreground mb-4">{resource.description}</p>
                      <Button variant="secondary" size="sm" className="mt-2">
                        Visit Resource
                      </Button>
                    </a>
                  ))
                )}
              </div>
            </div>
            
            {/* Sidebar - increased width */}
            <div className="lg:w-1/3">
              <div className="sticky top-24 bg-card border border-border/50 rounded-lg p-6">
                <h3 className="font-bold mb-4">Newsletter Benefits</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <div className="rounded-full bg-primary/10 p-1 mt-0.5">
                      <div className="h-2 w-2 rounded-full bg-primary"></div>
                    </div>
                    <span>AI Workflows that 10x your productivity</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="rounded-full bg-primary/10 p-1 mt-0.5">
                      <div className="h-2 w-2 rounded-full bg-primary"></div>
                    </div>
                    <span>Weekly deep-dives on product management and AI tech</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="rounded-full bg-primary/10 p-1 mt-0.5">
                      <div className="h-2 w-2 rounded-full bg-primary"></div>
                    </div>
                    <span>Insights to accelerate your product building journey</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="rounded-full bg-primary/10 p-1 mt-0.5">
                      <div className="h-2 w-2 rounded-full bg-primary"></div>
                    </div>
                    <span>Practical frameworks for effective product strategy</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="rounded-full bg-primary/10 p-1 mt-0.5">
                      <div className="h-2 w-2 rounded-full bg-primary"></div>
                    </div>
                    <span>Real-world case studies from industry leaders</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="rounded-full bg-primary/10 p-1 mt-0.5">
                      <div className="h-2 w-2 rounded-full bg-primary"></div>
                    </div>
                    <span>Exclusive access to product tools and resources</span>
                  </li>
                </ul>
                
                <Separator className="my-6" />
                
                <h3 className="font-bold mb-4">Subscribe to Newsletter</h3>
                <div className="flex justify-center">
                  <iframe 
                    src="https://sidsaladi.substack.com/embed" 
                    width="100%" 
                    height="400" 
                    style={{
                      border: "1px solid #EEE",
                      background: "white"
                    }} 
                    frameBorder="0" 
                    scrolling="no" 
                    title="Substack Newsletter Subscription"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPage;
